// src/views/accueil/BarreOperateur.jsx — LUI PARLER DEPUIS N'IMPORTE OÙ.
//
// Décision Dimitri, 2026-09-07 : « Je dois pouvoir lui parler depuis n'importe
// quelle page, mais tout doit atterrir dans le même historique. Sinon il perd
// le contexte. »
//
// ── OÙ ELLE SE MONTRE, ET OÙ ELLE NE SE MONTRE PAS ─────────────────────────
//   · ORDINATEUR, hors accueil, hors opérateur → la barre.
//   · ACCUEIL   → rien : le champ sous sa journée suffit.
//   · CHEZ LUI  → rien : on est déjà dans la conversation.
//   · TÉLÉPHONE → rien : le micro du bandeau reprend son rôle (déjà en place
//     depuis le 2026-08-26, arbitré APRÈS mesure — ne pas le refaire).
//
// ⚠️ LE MÊME SEUIL QUE `BoutonMicro` (useEtroit, 860 px). Deux définitions de
// « téléphone » qui divergent d'un pixel donnent un écran où le micro a disparu
// et où la barre n'est pas encore là.
//
// ── L'INVITE SUIT LA PAGE, LE FIL RESTE LE MÊME ────────────────────────────
// Le texte change (« une question sur l'argent… ») mais ce n'est QU'UN INDICE
// DE FORMULATION : on peut lui demander n'importe quoi depuis n'importe où, et
// tout va dans le même historique.
//
// ⚠️ ELLE NE REMPLACE PAS ENCORE LE MICRO FLOTTANT. Consigne Dimitri : « ne
// retire rien avant d'avoir le remplaçant ». Les deux coexistent tant que
// celle-ci n'est pas vérifiée à l'écran.

import { useState } from 'react'
import { DS } from '../../lib/ui'
import useEtroit from '../../lib/useEtroit'

const BORD = '#c3d5e4'

// L'invite par destination. Une page inconnue retombe sur la formule neutre —
// jamais un intitulé inventé qui laisserait croire à une compétence absente.
const INVITES = {
  jour: 'Une question sur la journée…',
  argent: "Une question sur l'argent…",
  logements: 'Une question sur un logement…',
  voyageurs: 'Une question sur un voyageur…',
  params: 'Une question sur les réglages…',
}
const NEUTRE = 'Une question, une consigne…'

// Maquette NEST v677 : « L'opérateur · Une question sur le calendrier ? »,
// l'invite suit la ZONE (Calendrier, Opérations, Tâches, Voyageurs).
const INVITES_ONGLET = {
  calendrier: 'Une question sur le calendrier ?',
  menages: 'Une question sur les opérations ?',
  'menage-controle': 'Une question sur les opérations ?',
  incidents: 'Une question sur les opérations ?',
  'orchestrateur-horaires': 'Une question sur les opérations ?',
  stock: 'Une question sur les opérations ?',
  'mes-taches': 'Une question sur vos tâches ?',
  messages: 'Une question sur les voyageurs ?',
  'voyageurs-textes': 'Une question sur les voyageurs ?',
  'voyageurs-demandes': 'Une question sur les voyageurs ?',
  'voyageurs-avis': 'Une question sur les voyageurs ?',
}

/** La famille de l'onglet courant — les ids internes ne bougent pas. */
export function familleDe(tab) {
  if (!tab) return null
  if (tab === 'accueil') return 'accueil'
  if (tab === 'operateur') return 'operateur'
  if (tab === 'params') return 'params'
  if (['calendrier', 'menages', 'menage-controle', 'incidents',
    'orchestrateur-horaires', 'stock', 'mes-taches'].includes(tab)) return 'jour'
  if (['donnees', 'facturation', 'historique', 'compta'].includes(tab)) return 'argent'
  if (tab === 'logements') return 'logements'
  if (tab.startsWith('voyageurs') || tab === 'messages') return 'voyageurs'
  return null
}

export default function BarreOperateur({ tab, onOuvrir }) {
  const etroit = useEtroit()
  const [brouillon, setBrouillon] = useState('')
  const [dit, setDit] = useState(false)
  const famille = familleDe(tab)

  // Les trois silences voulus. Ils se lisent d'un coup d'œil, sans commentaire.
  if (etroit) return null
  if (famille === 'accueil' || famille === 'operateur') return null

  return (
    <div data-testid="barre-operateur" style={{
      position: 'fixed', left: '50%', transform: 'translateX(-50%)', bottom: 18,
      zIndex: 34, width: 'min(420px, calc(100vw - 32px))', background: '#fff',
      border: `1px solid ${BORD}`, borderRadius: 15,
      boxShadow: '0 6px 26px rgba(35,50,64,.18)', fontFamily: DS.fontBody,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '8px 9px 8px 14px' }}>
        <b data-testid="barre-operateur-nom" style={{ fontSize: 12.5, fontWeight: 600, color: DS.textPrimary, whiteSpace: 'nowrap' }}>
          L&rsquo;opérateur <span aria-hidden="true" style={{ fontWeight: 400, color: DS.textMuted }}>·</span>
        </b>
        <input data-testid="barre-operateur-champ" value={brouillon}
          onChange={(e) => { setBrouillon(e.target.value); setDit(false) }}
          placeholder={INVITES_ONGLET[tab] || INVITES[famille] || NEUTRE}
          style={{
            flex: 1, minWidth: 0, fontSize: 12.5, color: DS.textPrimary, border: 'none',
            outline: 'none', background: 'transparent', fontFamily: DS.fontBody,
          }} />
        <button data-testid="barre-operateur-envoyer" onClick={() => setDit(true)}
          aria-label="Envoyer à l'opérateur"
          style={{
            width: 36, height: 36, borderRadius: 10, background: '#f8fafc',
            border: '1px solid #e3e8ee', color: DS.textMuted, display: 'flex',
            alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer',
          }}>↑</button>
        <button data-testid="barre-operateur-ouvrir" onClick={onOuvrir}
          aria-label="Ouvrir l'opérateur"
          style={{
            width: 36, height: 36, borderRadius: 10, background: DS.ciel, color: '#fff',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0, fontSize: 14, border: 'none', cursor: 'pointer',
          }}>›</button>
      </div>
      {/* Il ne fait pas semblant — même règle que le champ de l'accueil. */}
      {dit && (
        <div data-testid="barre-operateur-inerte" style={{
          borderTop: `1px solid ${BORD}`, padding: '8px 14px 10px',
          fontSize: 11, color: DS.textMuted, lineHeight: 1.5,
        }}>
          Il ne sait pas encore répondre — sa conversation n&rsquo;est pas ouverte.
          Rien n&rsquo;a été envoyé.
        </div>
      )}
    </div>
  )
}
